/**
 * SalesOrderDataPanel
 *
 * Displays extracted sales order data with hoverable fields
 * for citation highlighting in the document viewer.
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type {
  SalesOrderDraft,
  LineItem,
} from '@/types/application/SalesOrderDraftDTO';
import type {
  Citation,
  ConfidenceScores,
} from '@/components/document-comparison/types';
import type { DataColumnRenderContext } from '@/components/document-comparison/columns/types';
import { buildFieldPath } from '@/components/document-comparison/hooks/useCitationMapping';
import { LabeledField, TableFieldCell } from './FieldCell';
import { ConfidenceIndicator } from './ConfidenceBadge';

interface SalesOrderDataPanelProps {
  /** The sales order draft data */
  draft: SalesOrderDraft;
  /** Context from the data column */
  context: DataColumnRenderContext;
  /** Which purchase order index this draft corresponds to (for multi-PO documents) */
  purchaseOrderIndex?: number;
}

/**
 * Format a date value for display.
 */
function formatDate(value: Date | string | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return date.toLocaleDateString();
}

/**
 * Format a currency value for display.
 */
function formatCurrency(value: number | string | undefined): string {
  if (value === undefined || value === null) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export function SalesOrderDataPanel({
  draft,
  context,
  purchaseOrderIndex = 0,
}: SalesOrderDataPanelProps) {
  const {
    hoveredFieldPath,
    lockedFieldPath,
    onFieldHover,
    onFieldClick,
    getCitationsForField,
    getConfidenceForField,
  } = context;

  // Build field path helper that includes the purchase order index
  const getPath = (field: string) =>
    buildFieldPath(`purchase_orders`, purchaseOrderIndex, field);

  // Helper to check if a field is highlighted
  const isFieldHighlighted = (path: string) =>
    hoveredFieldPath === path || lockedFieldPath === path;

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <HeaderSection
        draft={draft}
        getPath={getPath}
        hoveredFieldPath={hoveredFieldPath}
        lockedFieldPath={lockedFieldPath}
        onFieldHover={onFieldHover}
        onFieldClick={onFieldClick}
        getCitationsForField={getCitationsForField}
        getConfidenceForField={getConfidenceForField}
      />

      <Separator />

      {/* Destination Address */}
      {draft.destinationAddress && (
        <>
          <AddressSection
            title="Ship To Address"
            address={draft.destinationAddress}
            basePath={getPath('destination_address')}
            hoveredFieldPath={hoveredFieldPath}
            lockedFieldPath={lockedFieldPath}
            onFieldHover={onFieldHover}
            onFieldClick={onFieldClick}
            getCitationsForField={getCitationsForField}
            getConfidenceForField={getConfidenceForField}
          />
          <Separator />
        </>
      )}

      {/* Line Items */}
      <LineItemsSection
        lineItems={draft.lineItems ?? []}
        purchaseOrderIndex={purchaseOrderIndex}
        hoveredFieldPath={hoveredFieldPath}
        lockedFieldPath={lockedFieldPath}
        onFieldHover={onFieldHover}
        onFieldClick={onFieldClick}
        getCitationsForField={getCitationsForField}
        getConfidenceForField={getConfidenceForField}
      />

      {/* Totals */}
      {(draft.totalAmount || draft.extractedTotalAmount) && (
        <>
          <Separator />
          <TotalsSection
            draft={draft}
            getPath={getPath}
            hoveredFieldPath={hoveredFieldPath}
            lockedFieldPath={lockedFieldPath}
            onFieldHover={onFieldHover}
            onFieldClick={onFieldClick}
            getCitationsForField={getCitationsForField}
            getConfidenceForField={getConfidenceForField}
          />
        </>
      )}
    </div>
  );
}

// ============================================================================
// Section Components
// ============================================================================

interface SectionProps {
  hoveredFieldPath: string | null;
  lockedFieldPath: string | null;
  onFieldHover: (path: string | null) => void;
  onFieldClick: (path: string) => void;
  getCitationsForField: (path: string) => Citation[];
  getConfidenceForField: (path: string) => ConfidenceScores | null;
}

interface HeaderSectionProps extends SectionProps {
  draft: SalesOrderDraft;
  getPath: (field: string) => string;
}

function HeaderSection({
  draft,
  getPath,
  hoveredFieldPath,
  lockedFieldPath,
  onFieldHover,
  onFieldClick,
  getCitationsForField,
  getConfidenceForField,
}: HeaderSectionProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-3">Order Information</h3>
      <div className="grid grid-cols-2 gap-4">
        <LabeledField
          label="PO Number"
          path={getPath('purchase_order_number')}
          value={draft.purchaseOrderNumber}
          isHovered={hoveredFieldPath === getPath('purchase_order_number')}
          isLocked={lockedFieldPath === getPath('purchase_order_number')}
          confidence={
            getConfidenceForField(getPath('purchase_order_number')) ?? undefined
          }
          citations={getCitationsForField(getPath('purchase_order_number'))}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="Order Date"
          path={getPath('order_date')}
          value={formatDate(draft.orderDate)}
          isHovered={hoveredFieldPath === getPath('order_date')}
          isLocked={lockedFieldPath === getPath('order_date')}
          confidence={getConfidenceForField(getPath('order_date')) ?? undefined}
          citations={getCitationsForField(getPath('order_date'))}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="Delivery Date"
          path={getPath('delivery_date')}
          value={formatDate(draft.deliveryDate)}
          isHovered={hoveredFieldPath === getPath('delivery_date')}
          isLocked={lockedFieldPath === getPath('delivery_date')}
          confidence={
            getConfidenceForField(getPath('delivery_date')) ?? undefined
          }
          citations={getCitationsForField(getPath('delivery_date'))}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="Ship Date"
          path={getPath('ship_date')}
          value={formatDate(draft.shipDate)}
          isHovered={hoveredFieldPath === getPath('ship_date')}
          isLocked={lockedFieldPath === getPath('ship_date')}
          confidence={getConfidenceForField(getPath('ship_date')) ?? undefined}
          citations={getCitationsForField(getPath('ship_date'))}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="Currency"
          path={getPath('currency')}
          value={draft.currency}
          isHovered={hoveredFieldPath === getPath('currency')}
          isLocked={lockedFieldPath === getPath('currency')}
          confidence={getConfidenceForField(getPath('currency')) ?? undefined}
          citations={getCitationsForField(getPath('currency'))}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />
      </div>
    </div>
  );
}

interface AddressSectionProps extends SectionProps {
  title: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  basePath: string;
}

function AddressSection({
  title,
  address,
  basePath,
  hoveredFieldPath,
  lockedFieldPath,
  onFieldHover,
  onFieldClick,
  getCitationsForField,
  getConfidenceForField,
}: AddressSectionProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        <LabeledField
          label="Street"
          path={`${basePath}.street`}
          value={address.street}
          isHovered={hoveredFieldPath === `${basePath}.street`}
          isLocked={lockedFieldPath === `${basePath}.street`}
          confidence={getConfidenceForField(`${basePath}.street`) ?? undefined}
          citations={getCitationsForField(`${basePath}.street`)}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="City"
          path={`${basePath}.city`}
          value={address.city}
          isHovered={hoveredFieldPath === `${basePath}.city`}
          isLocked={lockedFieldPath === `${basePath}.city`}
          confidence={getConfidenceForField(`${basePath}.city`) ?? undefined}
          citations={getCitationsForField(`${basePath}.city`)}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="State"
          path={`${basePath}.state`}
          value={address.state}
          isHovered={hoveredFieldPath === `${basePath}.state`}
          isLocked={lockedFieldPath === `${basePath}.state`}
          confidence={getConfidenceForField(`${basePath}.state`) ?? undefined}
          citations={getCitationsForField(`${basePath}.state`)}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="ZIP Code"
          path={`${basePath}.zip_code`}
          value={address.zipCode}
          isHovered={hoveredFieldPath === `${basePath}.zip_code`}
          isLocked={lockedFieldPath === `${basePath}.zip_code`}
          confidence={
            getConfidenceForField(`${basePath}.zip_code`) ?? undefined
          }
          citations={getCitationsForField(`${basePath}.zip_code`)}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />

        <LabeledField
          label="Country"
          path={`${basePath}.country`}
          value={address.country}
          isHovered={hoveredFieldPath === `${basePath}.country`}
          isLocked={lockedFieldPath === `${basePath}.country`}
          confidence={getConfidenceForField(`${basePath}.country`) ?? undefined}
          citations={getCitationsForField(`${basePath}.country`)}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />
      </div>
    </div>
  );
}

interface LineItemsSectionProps extends SectionProps {
  lineItems: LineItem[];
  purchaseOrderIndex: number;
}

function LineItemsSection({
  lineItems,
  purchaseOrderIndex,
  hoveredFieldPath,
  lockedFieldPath,
  onFieldHover,
  onFieldClick,
  getCitationsForField,
  getConfidenceForField,
}: LineItemsSectionProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-3">
        Line Items ({lineItems.length})
      </h3>

      {lineItems.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No line items</p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead>Identifier</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item, index) => (
                <LineItemRow
                  key={index}
                  item={item}
                  index={index}
                  purchaseOrderIndex={purchaseOrderIndex}
                  hoveredFieldPath={hoveredFieldPath}
                  lockedFieldPath={lockedFieldPath}
                  onFieldHover={onFieldHover}
                  onFieldClick={onFieldClick}
                  getCitationsForField={getCitationsForField}
                  getConfidenceForField={getConfidenceForField}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

interface LineItemRowProps extends SectionProps {
  item: LineItem;
  index: number;
  purchaseOrderIndex: number;
}

function LineItemRow({
  item,
  index,
  purchaseOrderIndex,
  hoveredFieldPath,
  lockedFieldPath,
  onFieldHover,
  onFieldClick,
  getCitationsForField,
  getConfidenceForField,
}: LineItemRowProps) {
  // Build field paths for this line item
  const getItemPath = (field: string) =>
    `purchase_orders[${purchaseOrderIndex}].line_items[${index}].${field}`;

  // Also check the row-level path (line_items[0] without sub-field)
  const rowPath = `purchase_orders[${purchaseOrderIndex}].line_items[${index}]`;

  const fields = [
    { key: 'customer_identifier', value: item.customerIdentifier },
    { key: 'customer_description', value: item.customerDescription },
    { key: 'quantity', value: item.quantity },
    { key: 'unit_price', value: item.unitPrice },
    { key: 'amount', value: item.amount },
  ];

  return (
    <TableRow>
      <TableCell className="font-mono text-muted-foreground">
        {index + 1}
      </TableCell>

      <TableCell>
        <LineItemCell
          path={getItemPath('customer_identifier')}
          rowPath={rowPath}
          value={item.customerIdentifier ?? '—'}
          hoveredFieldPath={hoveredFieldPath}
          lockedFieldPath={lockedFieldPath}
          onFieldHover={onFieldHover}
          onFieldClick={onFieldClick}
          getCitationsForField={getCitationsForField}
          getConfidenceForField={getConfidenceForField}
        />
      </TableCell>

      <TableCell>
        <LineItemCell
          path={getItemPath('customer_description')}
          rowPath={rowPath}
          value={item.customerDescription ?? '—'}
          hoveredFieldPath={hoveredFieldPath}
          lockedFieldPath={lockedFieldPath}
          onFieldHover={onFieldHover}
          onFieldClick={onFieldClick}
          getCitationsForField={getCitationsForField}
          getConfidenceForField={getConfidenceForField}
          className="max-w-[200px] truncate"
        />
      </TableCell>

      <TableCell className="text-right">
        <LineItemCell
          path={getItemPath('quantity')}
          rowPath={rowPath}
          value={item.quantity ?? '—'}
          hoveredFieldPath={hoveredFieldPath}
          lockedFieldPath={lockedFieldPath}
          onFieldHover={onFieldHover}
          onFieldClick={onFieldClick}
          getCitationsForField={getCitationsForField}
          getConfidenceForField={getConfidenceForField}
        />
      </TableCell>

      <TableCell className="text-right">
        <LineItemCell
          path={getItemPath('unit_price')}
          rowPath={rowPath}
          value={formatCurrency(item.unitPrice)}
          hoveredFieldPath={hoveredFieldPath}
          lockedFieldPath={lockedFieldPath}
          onFieldHover={onFieldHover}
          onFieldClick={onFieldClick}
          getCitationsForField={getCitationsForField}
          getConfidenceForField={getConfidenceForField}
        />
      </TableCell>

      <TableCell className="text-right">
        <LineItemCell
          path={getItemPath('amount')}
          rowPath={rowPath}
          value={formatCurrency(item.amount)}
          hoveredFieldPath={hoveredFieldPath}
          lockedFieldPath={lockedFieldPath}
          onFieldHover={onFieldHover}
          onFieldClick={onFieldClick}
          getCitationsForField={getCitationsForField}
          getConfidenceForField={getConfidenceForField}
        />
      </TableCell>
    </TableRow>
  );
}

interface LineItemCellProps extends SectionProps {
  path: string;
  rowPath: string;
  value: React.ReactNode;
  className?: string;
}

function LineItemCell({
  path,
  rowPath,
  value,
  hoveredFieldPath,
  lockedFieldPath,
  onFieldHover,
  onFieldClick,
  getCitationsForField,
  getConfidenceForField,
  className,
}: LineItemCellProps) {
  // Check both the specific field path and the row-level path for citations
  const fieldCitations = getCitationsForField(path);
  const rowCitations = getCitationsForField(rowPath);
  const hasCitation = fieldCitations.length > 0 || rowCitations.length > 0;

  // Use the field-level path for highlighting if it has citations, otherwise use row path
  const activePath = fieldCitations.length > 0 ? path : rowPath;

  return (
    <TableFieldCell
      path={activePath}
      value={value}
      isHovered={hoveredFieldPath === activePath}
      isLocked={lockedFieldPath === activePath}
      hasCitation={hasCitation}
      onHover={(hovering) => onFieldHover(hovering ? activePath : null)}
      onClick={() => onFieldClick(activePath)}
      className={className}
    />
  );
}

interface TotalsSectionProps extends SectionProps {
  draft: SalesOrderDraft;
  getPath: (field: string) => string;
}

function TotalsSection({
  draft,
  getPath,
  hoveredFieldPath,
  lockedFieldPath,
  onFieldHover,
  onFieldClick,
  getCitationsForField,
  getConfidenceForField,
}: TotalsSectionProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-3">Totals</h3>
      <div className="grid grid-cols-2 gap-4">
        <LabeledField
          label="Total Amount"
          path={getPath('total_amount')}
          value={formatCurrency(
            draft.extractedTotalAmount ?? draft.totalAmount,
          )}
          isHovered={hoveredFieldPath === getPath('total_amount')}
          isLocked={lockedFieldPath === getPath('total_amount')}
          confidence={
            getConfidenceForField(getPath('total_amount')) ?? undefined
          }
          citations={getCitationsForField(getPath('total_amount'))}
          onHover={onFieldHover}
          onClick={onFieldClick}
        />
      </div>
    </div>
  );
}
